import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const WIDTH = 700;
const HEIGHT = 420;
const MARGIN = { top: 20, right: 20, bottom: 55, left: 65 };

const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6'];

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Init. Visit',
  mosquitoTickControl: 'Mosquito/Tick',
};

const styles = {
  card: {
    position: 'relative',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  dropdown: {
    padding: '4px 8px',
    fontSize: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    color: '#374151',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    cursor: 'pointer',
    outline: 'none',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginTop: '16px',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  statItem: {
    textAlign: 'center',
  },
  statLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    marginBottom: '2px',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
};

export default function QQPlot({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedField, setSelectedField] = useState(null);

  // Initialize selectedField when data arrives
  useEffect(() => {
    if (data && data.fields && data.fields.length > 0 && selectedField === null) {
      setSelectedField(data.fields[0].field);
    }
  }, [data]);

  useEffect(() => {
    if (!data || !data.fields || data.fields.length === 0 || !selectedField) return;

    const fieldData = data.fields.find((f) => f.field === selectedField);
    if (!fieldData || !fieldData.points || fieldData.points.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : WIDTH;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const fieldIndex = data.fields.findIndex((f) => f.field === selectedField);
    const color = COLORS[fieldIndex % COLORS.length];
    const points = fieldData.points;

    // Compute extents
    const xExtent = d3.extent(points, (d) => d.theoretical);
    const yExtent = d3.extent(points, (d) => d.observed);
    const xPad = (xExtent[1] - xExtent[0]) * 0.1 || 0.5;
    const yPad = (yExtent[1] - yExtent[0]) * 0.1 || 1;

    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPad, xExtent[1] + xPad])
      .nice()
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([yExtent[0] - yPad, yExtent[1] + yPad])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    g.selectAll('.grid-h')
      .data(y.ticks(8))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Vertical grid lines
    g.selectAll('.grid-v')
      .data(x.ticks(8))
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Reference diagonal line (where points would fall if perfectly normal)
    // The line maps theoretical quantiles to expected observed values: observed = mean + stdDev * theoretical
    const mean = fieldData.mean;
    const stdDev = fieldData.stdDev;

    if (mean != null && stdDev != null && stdDev > 0) {
      // Compute the diagonal line endpoints within the visible x domain
      const xDomain = x.domain();
      const lineY0 = mean + stdDev * xDomain[0];
      const lineY1 = mean + stdDev * xDomain[1];

      g.append('line')
        .attr('x1', x(xDomain[0]))
        .attr('y1', y(lineY0))
        .attr('x2', x(xDomain[1]))
        .attr('y2', y(lineY1))
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4');

      // Label the reference line
      g.append('text')
        .attr('x', x(xDomain[1]) - 4)
        .attr('y', y(lineY1) - 8)
        .attr('text-anchor', 'end')
        .attr('font-size', '10px')
        .attr('fill', '#9ca3af')
        .text('Normal reference');
    }

    // Plot scatter points
    points.forEach((pt) => {
      g.append('circle')
        .attr('cx', x(pt.theoretical))
        .attr('cy', y(pt.observed))
        .attr('r', 5)
        .attr('fill', color)
        .attr('opacity', 0.75)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget).attr('r', 8).attr('opacity', 1);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            theoretical: pt.theoretical,
            observed: pt.observed,
            field: selectedField,
            color,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget).attr('r', 5).attr('opacity', 0.75);
          setTooltip(null);
        });
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Theoretical Quantiles (Normal)');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(8)
          .tickSize(0)
          .tickFormat((v) => `$${v}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -50)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Observed Quantiles ($)');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, selectedField]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Q-Q Plot (Normality Assessment)</div>
        <div style={styles.subtitle}>Compare price distributions against normal distribution</div>
        <div style={styles.emptyState}>No Q-Q plot data available</div>
      </div>
    );
  }

  const fieldData = data.fields.find((f) => f.field === selectedField);
  const fieldIndex = data.fields.findIndex((f) => f.field === selectedField);
  const activeColor = COLORS[fieldIndex % COLORS.length];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Q-Q Plot (Normality Assessment)</div>
        </div>
      </div>
      <div style={styles.subtitle}>Compare price distributions against normal distribution</div>

      {/* Field selector dropdown */}
      <div style={styles.controls}>
        <select
          style={styles.dropdown}
          value={selectedField || ''}
          onChange={(e) => setSelectedField(e.target.value)}
        >
          {data.fields.map((f, i) => (
            <option key={f.field} value={f.field}>
              {FIELD_LABELS[f.field] || f.field}
            </option>
          ))}
        </select>
        <span
          style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: activeColor,
          }}
        />
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {fieldData ? `${fieldData.points.length} points` : ''}
        </span>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Summary stats */}
      {fieldData && (
        <div style={styles.statsGrid}>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>Mean</div>
            <div style={styles.statValue}>
              {fieldData.mean != null ? `$${fieldData.mean.toFixed(2)}` : '--'}
            </div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>Std Dev</div>
            <div style={styles.statValue}>
              {fieldData.stdDev != null ? `$${fieldData.stdDev.toFixed(2)}` : '--'}
            </div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>Skewness</div>
            <div style={{
              ...styles.statValue,
              color: fieldData.skewness != null && Math.abs(fieldData.skewness) > 1 ? '#dc2626' : '#1f2937',
            }}>
              {fieldData.skewness != null ? fieldData.skewness.toFixed(3) : '--'}
            </div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.statLabel}>Kurtosis</div>
            <div style={{
              ...styles.statValue,
              color: fieldData.kurtosis != null && Math.abs(fieldData.kurtosis - 3) > 1 ? '#dc2626' : '#1f2937',
            }}>
              {fieldData.kurtosis != null ? fieldData.kurtosis.toFixed(3) : '--'}
            </div>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {FIELD_LABELS[tooltip.field] || tooltip.field}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Theoretical</span>
            <span style={{ fontWeight: 600 }}>{tooltip.theoretical.toFixed(3)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Observed</span>
            <span style={{ fontWeight: 600 }}>${tooltip.observed.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
